import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { HOME_PATH } from '../constants/navigation';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [formData, setFormData] = useState({
    identifier: '',
    email: '',
    username: '',
    password: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 보호 경로에서 넘어온 경우만 원래 경로로, 로그인 화면 자신이 오면 홈으로 (이동 루프 방지)
  const requestedPath = location.state?.from?.pathname;
  const from = requestedPath && requestedPath !== '/login' ? requestedPath : HOME_PATH;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError('');
    setFieldErrors(prev => ({
      ...prev,
      [name]: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const trimmedData = {
      identifier: formData.identifier.trim(),
      email: formData.email.trim(),
      username: formData.username.trim(),
      password: formData.password,
    };

    const validationErrors = {};
    const usernamePattern = /^[a-zA-Z0-9_]{3,20}$/;

    if (showRegister) {
      if (!trimmedData.email) {
        validationErrors.email = '이메일을 입력해주세요.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedData.email)) {
        validationErrors.email = '올바른 이메일 주소를 입력해주세요.';
      }

      if (!trimmedData.username) {
        validationErrors.username = '아이디를 입력해주세요.';
      } else if (!usernamePattern.test(trimmedData.username)) {
        validationErrors.username = '아이디는 3~20자의 영문, 숫자, 밑줄만 사용할 수 있습니다.';
      }

      if (!trimmedData.password) {
        validationErrors.password = '비밀번호를 입력해주세요.';
      } else if (trimmedData.password.length < 6) {
        validationErrors.password = '비밀번호는 최소 6자리 이상이어야 합니다.';
      }
    } else {
      if (!trimmedData.identifier || !trimmedData.password) {
        setError('이메일/사용자명과 비밀번호를 입력해주세요.');
        return;
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    setIsLoading(true);

    try {
      if (showRegister) {
        const result = await register({
          email: trimmedData.email,
          username: trimmedData.username,
          password: trimmedData.password,
        });

        if (result.success) {
          setError('');
          setShowRegister(false);
          setFormData({
            identifier: trimmedData.email || trimmedData.username,
            email: '',
            username: '',
            password: '',
          });
          alert(`회원가입이 완료되었습니다.\n관리자 승인 후 로그인이 가능합니다.\n\n사용자: ${result.data?.username}\n이메일: ${result.data?.email}\n\n승인 상태: ${result.data?.is_approved ? '승인됨' : '대기중'}`);
        } else {
          const message = result.message || '회원가입에 실패했습니다.';
          const loweredMessage = message.toLowerCase();

          if (loweredMessage.includes('email')) {
            setFieldErrors(prev => ({ ...prev, email: message }));
          }
          if (loweredMessage.includes('username')) {
            setFieldErrors(prev => ({ ...prev, username: message }));
          }

          setError(message);
        }
      } else {
        const result = await login(trimmedData.identifier, trimmedData.password);
        if (result.success) {
          navigate(from, { replace: true });
        } else {
          setError(result.message);
        }
      }
    } catch (error) {
      setError('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/stack_front.png`}
              alt="StackStock 캐릭터"
              sx={{
                width: { xs: 50, sm: 60 },
                height: 'auto',
              }}
            />
            <Typography component="h1" variant="h4" sx={{ fontWeight: 600 }}>
              StackStock
            </Typography>
          </Box>
          
          <Typography variant="h5" sx={{ mb: 3 }}>
            {showRegister ? '회원가입' : '로그인'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {showRegister ? (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="register-email"
                  label="이메일"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  placeholder="example@email.com"
                  error={Boolean(fieldErrors.email)}
                  helperText={fieldErrors.email || '로그인 승인 안내 메일이 전송됩니다.'}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="register-username"
                  label="아이디"
                  name="username"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  placeholder="영문, 숫자, 밑줄(_) 3~20자"
                  error={Boolean(fieldErrors.username)}
                  helperText={fieldErrors.username || '영문, 숫자, 밑줄(_) 조합 3~20자'}
                />
              </>
            ) : (
              <TextField
                margin="normal"
                required
                fullWidth
                id="login-identifier"
                label="이메일 또는 사용자명"
                name="identifier"
                autoComplete="username"
                autoFocus
                value={formData.identifier}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="이메일 또는 사용자명"
                error={Boolean(fieldErrors.identifier)}
                helperText={fieldErrors.identifier || ''}
              />
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="비밀번호"
              type="password"
              id="password"
              autoComplete={showRegister ? 'new-password' : 'current-password'}
              value={formData.password}
              onChange={handleInputChange}
              disabled={isLoading}
              error={Boolean(fieldErrors.password)}
              helperText={fieldErrors.password || (showRegister ? '최소 6자리 이상 입력해주세요' : '')}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                showRegister ? '회원가입' : '로그인'
              )}
            </Button>

            <Divider sx={{ my: 2 }} />

            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary">
                {showRegister ? '이미 계정이 있으신가요?' : '계정이 없으신가요?'}
              </Typography>
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setShowRegister(!showRegister);
                  setError('');
                  setFormData({
                    identifier: '',
                    email: '',
                    username: '',
                    password: '',
                  });
                  setFieldErrors({});
                }}
                disabled={isLoading}
                sx={{ mt: 1 }}
              >
                {showRegister ? '로그인하기' : '회원가입하기'}
              </Link>
            </Box>
          </Box>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              개인 주식 포트폴리오 관리 시스템
            </Typography>
            <Typography variant="body2" color="text.secondary">
              수익률 계산 및 데이터 시각화
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
